<template>
  <div class="medium-editor-page">
    <!-- Top Navbar -->
    <div class="medium-navbar">
      <div class="d-flex align-items-center">
        <span class="d-flex align-items-center font-weight-bolder text-dark mr-3">
          <feather-icon icon="BookOpenIcon" size="22" class="text-primary mr-1" />
          <span>Story Editor</span>
        </span>
      </div>
      <div class="medium-navbar-right">
        <select class="medium-lang-select mr-2" v-model="model.lang">
          <option value="id">🇮🇩 ID</option>
          <option value="en">🇬🇧 EN</option>
        </select>
        <select class="medium-status-select mr-2" v-model="model.status">
          <option value="inactive">📝 Draft</option>
          <option value="active">🟢 Aktif</option>
        </select>
        <button class="medium-publish-btn" @click="save" :disabled="saving">
          <span v-if="saving">Menyimpan...</span>
          <span v-else>{{ $route.meta.action === 'edit' ? 'Update' : 'Publish' }}</span>
        </button>
        <button class="medium-back-btn ml-2" @click="$router.push({ name: 'stories' })">
          <feather-icon icon="XIcon" size="18" />
        </button>
      </div>
    </div>

    <!-- Error Banner -->
    <div v-if="showDismissibleAlert" class="medium-error-banner">
      {{ messages }}
      <button @click="showDismissibleAlert = false" class="medium-error-close">×</button>
    </div>

    <!-- Main Content Area -->
    <div class="medium-content-area" @click="focusLastTextBlock">

      <!-- Cover Image -->
      <div class="medium-cover-section">
        <div v-if="!imagePreview" class="medium-cover-placeholder" @click.stop="triggerImageUpload">
          <feather-icon icon="ImageIcon" size="32" class="text-muted" />
          <p class="mt-2 text-muted small">Klik untuk menambahkan gambar sampul</p>
        </div>
        <div v-else class="medium-cover-preview" @click.stop="triggerImageUpload" style="cursor: pointer;">
          <img :src="imagePreview" alt="Cover" />
          <button class="medium-cover-remove" @click.stop="removeCover">
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>
        <input
          ref="coverInput"
          type="file"
          accept="image/*"
          style="display:none"
          @change="onImageChange"
        />
      </div>

      <!-- Title Input -->
      <div class="medium-title-wrap">
        <textarea
          ref="titleInput"
          class="medium-title-input"
          placeholder="Judul Story..."
          v-model="model.title"
          rows="1"
          @input="autoResizeTitle"
          @keydown.enter.prevent="focusFirstBlock"
        ></textarea>
      </div>

      <!-- Blocks Flow -->
      <div class="medium-blocks">
        <div
          v-for="(block, index) in model.data.blocks"
          :key="block._uid"
          class="medium-block-wrapper"
          @mouseenter="hoveredBlock = index"
          @mouseleave="hoveredBlock = null"
        >
          <!-- Text Block -->
          <div v-if="block.type === 'text'" class="medium-text-block">
            <quill-editor
              :ref="'editor_' + index"
              v-model="block.content"
              :options="editorOption"
              class="medium-quill"
            />
          </div>

          <!-- Image Block -->
          <div v-else-if="block.type === 'image'" class="medium-image-block">
            <div v-if="!block.imagePreview && !block.imageUrl" class="medium-image-placeholder" @click="triggerBlockImageUpload(index)">
              <feather-icon icon="ImageIcon" size="32" class="text-muted" />
              <p class="mt-1 text-muted small">Klik untuk memilih gambar</p>
              <input
                :ref="'imgInput_' + index"
                type="file"
                accept="image/*"
                style="display:none"
                @change="e => onBlockImageChange(e, block)"
              />
            </div>
            <div v-else class="medium-image-preview-wrap">
              <img :src="block.imagePreview || block.image_url" :alt="block.caption || 'gambar'" />
              <input
                class="medium-image-caption"
                v-model="block.caption"
                placeholder="Tulis keterangan gambar (opsional)..."
              />
              <button class="medium-block-remove" @click.stop="removeBlock(index)">
                <feather-icon icon="TrashIcon" size="14" />
              </button>
            </div>
          </div>

          <!-- Chart Block -->
          <div v-else-if="block.type === 'chart' || block.type === 'map'" class="medium-chart-block">
            <div class="medium-chart-header">
              <feather-icon :icon="block.type === 'chart' ? 'BarChart2Icon' : 'MapIcon'" class="mr-1" />
              <span>{{ block.type === 'chart' ? 'Grafik Data Interaktif' : 'Peta Data Interaktif' }}</span>
              <button class="medium-block-remove-inline ml-auto" @click="removeBlock(index)">
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
            <div class="medium-chart-body">
              <input
                class="medium-chart-caption mb-3 font-weight-bold text-dark"
                style="font-size: 1.1rem; border-top: none; padding-top: 0;"
                v-model="block.title"
                placeholder="Ketik judul grafik/peta di sini..."
              />
              <div class="medium-chart-selector">
                <label class="medium-chart-label">Pilih Indikator Data</label>
                <v-select
                  v-model="block.indicator_id"
                  :options="indicators"
                  :reduce="i => i.id"
                  label="name"
                  placeholder="Cari indikator..."
                  @input="fetchAvailableYears($event)"
                >
                  <template #option="{ name, category_indicator }">
                    <span class="font-weight-bolder">{{ name }}</span>
                    <br /><small class="text-muted">{{ category_indicator ? category_indicator.name : '' }}</small>
                  </template>
                </v-select>
              </div>

              <!-- Interactive Chart Builder (Live Preview & Filters) -->
              <div class="mt-3">
                <chart-builder 
                  v-if="block.type === 'chart' && block.indicator_id"
                  :indicator-id="block.indicator_id"
                  :title="getIndicatorInfo(block.indicator_id) ? getIndicatorInfo(block.indicator_id).name : 'Grafik Data'"
                  :indicators="indicators"
                  v-model="block.filters"
                />
              </div>

              <!-- Interactive Map Builder (Live Preview & Filters) -->
              <div class="mt-3">
                <map-preview 
                  v-if="block.type === 'map' && block.indicator_id"
                  :indicator-id="block.indicator_id"
                  :title="getIndicatorInfo(block.indicator_id) ? getIndicatorInfo(block.indicator_id).name : 'Peta Data'"
                  :available-years="availableYears[block.indicator_id] || [String(block.filters.custom_time || '2021')]"
                  v-model="block.filters"
                />
              </div>

              <!-- Indicator Info Preview (For Map) -->
              <div v-if="block.type === 'map' && block.indicator_id && getIndicatorInfo(block.indicator_id)" class="medium-indicator-info mt-2">
                <feather-icon icon="CheckCircleIcon" class="text-success mr-1" size="14" />
                <span>{{ getIndicatorInfo(block.indicator_id).name }}</span>
                <span class="text-muted ml-2 small">· {{ getIndicatorInfo(block.indicator_id).category_indicator ? getIndicatorInfo(block.indicator_id).category_indicator.name : '' }}</span>
              </div>
              <!-- Caption -->
              <input
                class="medium-chart-caption"
                v-model="block.caption"
                placeholder="Tambahkan narasi pendukung untuk grafik ini (opsional)..."
              />
            </div>
          </div>

          <!-- Infobox Block (Cover Buku, Key Summary, Visit Site) -->
          <div v-else-if="block.type === 'infobox'" class="medium-infobox-block">
            <div class="medium-chart-header">
              <feather-icon icon="BookOpenIcon" class="mr-1 text-primary" />
              <span class="font-weight-bold text-dark">Add Infobox (Cover Buku & Key Summary)</span>
              <button class="medium-block-remove-inline ml-auto" @click="removeBlock(index)">
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
            <div class="medium-infobox-body p-4">
              <div class="row">
                <!-- Left: Book Cover -->
                <div class="col-md-5 col-lg-4 mb-3 mb-md-0">
                  <label class="medium-chart-label">Cover Buku / Laporan</label>
                  <div v-if="!block.imagePreview && !block.image_url" class="medium-infobox-placeholder" @click="triggerBlockImageUpload(index)">
                    <feather-icon icon="BookIcon" size="32" class="text-muted mb-2" />
                    <p class="mb-0 text-muted small font-weight-bold">Klik upload cover buku</p>
                    <small class="text-muted d-block mt-1" style="font-size: 0.75rem;">Format: JPG, PNG</small>
                    <input
                      :ref="'imgInput_' + index"
                      type="file"
                      accept="image/*"
                      style="display:none"
                      @change="e => onBlockImageChange(e, block)"
                    />
                  </div>
                  <div v-else class="medium-infobox-preview text-center">
                    <img :src="block.imagePreview || block.image_url" alt="Cover Buku" class="img-fluid rounded shadow-sm mb-2" style="max-height: 280px; object-fit: contain;" />
                    <div class="d-flex justify-content-center">
                      <button class="btn btn-sm btn-outline-primary mr-1" @click="triggerBlockImageUpload(index)">
                        <feather-icon icon="UploadIcon" size="12" class="mr-50" /> Ganti
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="removeBlockImage(block)">
                        <feather-icon icon="Trash2Icon" size="12" />
                      </button>
                    </div>
                    <input
                      :ref="'imgInput_' + index"
                      type="file"
                      accept="image/*"
                      style="display:none"
                      @change="e => onBlockImageChange(e, block)"
                    />
                  </div>
                </div>
                <!-- Right: Key Summary & Visit Site -->
                <div class="col-md-7 col-lg-8">
                  <div class="form-group mb-3">
                    <label class="medium-chart-label">Judul Buku / Infobox</label>
                    <input
                      class="form-control font-weight-bold text-dark"
                      v-model="block.title"
                      placeholder="Judul Buku atau Laporan (cth: Laporan Tahunan BEST 2025)..."
                    />
                  </div>
                  <div class="form-group mb-3">
                    <label class="medium-chart-label">Key Summary</label>
                    <textarea
                      class="form-control"
                      rows="5"
                      v-model="block.summary"
                      placeholder="Tuliskan ringkasan penting, poin-poin utama, atau latar belakang publikasi ini..."
                      style="line-height: 1.6;"
                    ></textarea>
                  </div>
                  <div class="row">
                    <div class="col-md-5 mb-2">
                      <label class="medium-chart-label">Teks Tombol</label>
                      <input
                        class="form-control"
                        v-model="block.button_text"
                        placeholder="cth: Visit Site / Beli Buku"
                      />
                    </div>
                    <div class="col-md-7 mb-2">
                      <label class="medium-chart-label">URL Kunjungi Situs (Visit Site Link)</label>
                      <input
                        class="form-control"
                        v-model="block.button_url"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- + Button: appears below each block -->
          <div class="medium-add-row" :class="{ 'medium-add-row-visible': hoveredBlock === index || addMenuOpenAt === index }">
            <div class="medium-add-btn-wrap">
              <button
                class="medium-add-btn"
                :class="{ 'medium-add-btn-active': addMenuOpenAt === index }"
                @click.stop="toggleAddMenu(index)"
              >
                <feather-icon icon="PlusIcon" size="18" />
              </button>
              <!-- Popup Menu -->
              <transition name="fade-pop">
                <div v-if="addMenuOpenAt === index" class="medium-add-menu" @click.stop>
                  <button class="medium-add-menu-item" @click="insertBlock('text', index)">
                    <feather-icon icon="TypeIcon" class="mr-1" /> Teks
                  </button>
                  <button class="medium-add-menu-item" @click="insertBlock('image', index)">
                    <feather-icon icon="ImageIcon" class="mr-1" /> Gambar
                  </button>
                  <button class="medium-add-menu-item" @click="insertBlock('chart', index)">
                    <feather-icon icon="BarChart2Icon" class="mr-1" /> Grafik Data
                  </button>
                  <button class="medium-add-menu-item" @click="insertBlock('map', index)">
                    <feather-icon icon="MapIcon" class="mr-1" /> Peta Data
                  </button>
                  <button class="medium-add-menu-item" @click="insertBlock('infobox', index)">
                    <feather-icon icon="BookOpenIcon" class="mr-1" /> Add Infobox
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <!-- Initial + button when no blocks or at the end -->
        <div class="medium-add-row medium-add-row-visible medium-initial-add" v-if="model.data.blocks.length === 0">
          <div class="medium-initial-hint">
            <span class="text-muted mr-3">Mulai tulis, atau tambahkan elemen...</span>
          </div>
          <div class="medium-add-btn-wrap">
            <button
              class="medium-add-btn"
              :class="{ 'medium-add-btn-active': addMenuOpenAt === -1 }"
              @click.stop="toggleAddMenu(-1)"
            >
              <feather-icon icon="PlusIcon" size="18" />
            </button>
            <transition name="fade-pop">
              <div v-if="addMenuOpenAt === -1" class="medium-add-menu" @click.stop>
                <button class="medium-add-menu-item" @click="insertBlock('text', -1)">
                  <feather-icon icon="TypeIcon" class="mr-1" /> Teks
                </button>
                <button class="medium-add-menu-item" @click="insertBlock('image', -1)">
                  <feather-icon icon="ImageIcon" class="mr-1" /> Gambar
                </button>
                <button class="medium-add-menu-item" @click="insertBlock('chart', -1)">
                  <feather-icon icon="BarChart2Icon" class="mr-1" /> Grafik Data
                </button>
                <button class="medium-add-menu-item" @click="insertBlock('map', -1)">
                  <feather-icon icon="MapIcon" class="mr-1" /> Peta Data
                </button>
                <button class="medium-add-menu-item" @click="insertBlock('infobox', -1)">
                  <feather-icon icon="BookOpenIcon" class="mr-1" /> Add Infobox
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Bottom padding -->
      <div style="height: 200px"></div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

/* ============================
   MEDIUM EDITOR - FULL LAYOUT
   ============================ */

.medium-editor-page {
  background: #fff;
  display: flex;
  flex-direction: column;
  font-family: 'Georgia', serif;
  border-radius: 0.5rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
}

/* Navbar */
.medium-navbar {
  height: 56px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
}
.medium-navbar-left {
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1rem;
}
.medium-logo {
  display: flex;
  align-items: center;
  color: #292929;
}
.medium-status-text {
  font-size: 0.8rem;
  font-weight: 400;
  color: #9b9b9b;
  font-family: 'Inter', sans-serif;
}
.medium-navbar-right {
  display: flex;
  align-items: center;
}
.medium-lang-select,
.medium-status-select {
  border: 1px solid #e8e8e8;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-family: 'Inter', sans-serif;
  background: #fff;
  cursor: pointer;
  outline: none;
}
.medium-publish-btn {
  background: #1a8917;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 7px 18px;
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #157013; }
  &:disabled { background: #a8d5a2; cursor: default; }
}
.medium-back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: #6b6b6b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  &:hover { background: #f0f0f0; }
}

/* Error Banner */
.medium-error-banner {
  background: #fde8e8;
  color: #c0392b;
  padding: 10px 24px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}
.medium-error-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #c0392b;
}

/* Content Area */
.medium-content-area {
  flex: 1;
  padding: 40px 20px;
}

/* Cover Image */
.medium-cover-section {
  max-width: 720px;
  margin: 0 auto 24px auto;
}
.medium-cover-placeholder {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover { border-color: #aaa; }
}
.medium-cover-preview {
  position: relative;
  img {
    width: 100%;
    max-height: 360px;
    object-fit: cover;
    border-radius: 8px;
  }
}
.medium-cover-remove {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* Title */
.medium-title-wrap {
  max-width: 720px;
  margin: 0 auto 8px auto;
}
.medium-title-input {
  width: 100%;
  border: none;
  outline: none;
  font-family: 'Georgia', serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #292929;
  line-height: 1.3;
  resize: none;
  overflow: hidden;
  background: transparent;
  padding: 0;
  &::placeholder { color: #d0d0d0; }
}

/* Blocks */
.medium-blocks {
  max-width: 720px;
  margin: 0 auto;
}

/* Block Wrapper */
.medium-block-wrapper {
  position: relative;
}

/* Text Block */
.medium-text-block {
  .medium-quill {
    .ql-container {
      border: none !important;
      font-family: 'Georgia', serif;
      font-size: 1.15rem;
      line-height: 1.85;
      color: #292929;
    }
    .ql-editor {
      padding: 4px 0;
      min-height: 60px;
      p {
        margin-bottom: 1.8rem;
      }
      p:last-child {
        margin-bottom: 0;
      }
      &.ql-blank::before {
        font-style: italic;
        color: #c0c0c0;
        left: 0;
      }
    }
    .ql-toolbar { display: none; }
  }
}

/* Image Block */
.medium-image-block {
  margin: 12px 0;
}
.medium-image-placeholder {
  border: 2px dashed #e0e0e0;
  border-radius: 6px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  &:hover { border-color: #aaa; }
}
.medium-image-preview-wrap {
  position: relative;
  text-align: center;
  img {
    max-width: 100%;
    max-height: 480px;
    object-fit: contain;
    border-radius: 4px;
  }
}
.medium-image-caption {
  display: block;
  width: 100%;
  border: none;
  outline: none;
  text-align: center;
  font-style: italic;
  font-size: 0.9rem;
  color: #9b9b9b;
  margin-top: 8px;
  background: transparent;
  &::placeholder { color: #ccc; }
}

/* Chart Block */
.medium-chart-block {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  margin: 12px 0;
}
.medium-chart-header {
  background: #f8f8f8;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #444;
  border-bottom: 1px solid #e8e8e8;
}
.medium-chart-body {
  padding: 16px;
  background: #fff;
}
.medium-chart-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
  margin-bottom: 6px;
}
.medium-chart-filters {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
.medium-filter-group {
  flex: 1;
  label {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #888;
    margin-bottom: 4px;
  }
  select {
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 0.85rem;
    font-family: 'Inter', sans-serif;
    outline: none;
    background: #fff;
    cursor: pointer;
  }
}
.medium-indicator-info {
  margin-top: 10px;
  padding: 8px 12px;
  background: #f0fff4;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}
.medium-chart-caption {
  display: block;
  width: 100%;
  border: none;
  outline: none;
  border-top: 1px solid #f0f0f0;
  margin-top: 12px;
  padding-top: 10px;
  font-style: italic;
  font-size: 0.9rem;
  color: #9b9b9b;
  background: transparent;
  &::placeholder { color: #ccc; }
}
.medium-block-remove {
  position: absolute;
  top: 8px; right: 8px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: #e74c3c;
  &:hover { background: #fde8e8; }
}
.medium-block-remove-inline {
  background: none;
  border: none;
  cursor: pointer;
  color: #bbb;
  display: flex;
  align-items: center;
  &:hover { color: #e74c3c; }
}

/* Infobox Block */
.medium-infobox-block {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  margin: 16px 0;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
}
.medium-infobox-body {
  background: #fff;
}
.medium-infobox-placeholder {
  border: 2px dashed #d8d8d8;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: all 0.2s;
  &:hover {
    border-color: #1a8917;
    background: #f0fff4;
  }
}
.medium-infobox-preview {
  position: relative;
  padding: 8px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #eeeeee;
}

/* + Button Row */
.medium-add-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  opacity: 0;
  transition: opacity 0.15s;
  &.medium-add-row-visible { opacity: 1; }
}
.medium-initial-add {
  margin-top: 8px;
}
.medium-initial-hint {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
}
.medium-add-btn-wrap {
  position: relative;
  display: inline-flex;
}
.medium-add-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #c0c0c0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  transition: all 0.15s;
  &:hover, &.medium-add-btn-active {
    border-color: #292929;
    color: #292929;
    background: #f8f8f8;
  }
  &.medium-add-btn-active {
    transform: rotate(45deg);
  }
}

/* Add Menu Popup */
.medium-add-menu {
  position: absolute;
  left: 44px;
  top: 50%;
  transform: translateY(-50%);
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  padding: 4px;
  z-index: 200;
  white-space: nowrap;
}
.medium-add-menu-item {
  background: none;
  border: none;
  padding: 8px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  color: #444;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: background 0.1s;
  &:hover { background: #f5f5f5; color: #1a8917; }
}

/* Chart selector override */
.medium-chart-selector .vs__dropdown-toggle {
  border-radius: 6px;
  border-color: #e0e0e0;
}

/* Animations */
.fade-pop-enter-active, .fade-pop-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.fade-pop-enter, .fade-pop-leave-to {
  opacity: 0;
  transform: translateY(-50%) scale(0.95);
}
</style>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import axios from '@axios'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { quillEditor } from 'vue-quill-editor'
import ChartBuilder from './ChartBuilder.vue'
import MapPreview from './MapPreview.vue'

const dynamicHost = typeof window !== 'undefined' && window.location && window.location.hostname ? window.location.hostname : 'localhost'
const API_BASE = process.env.VUE_APP_API_URL && !process.env.VUE_APP_API_URL.includes('localhost') ? process.env.VUE_APP_API_URL.replace(/\/$/, '') : `http://${dynamicHost}:3008`

let uid = 0

export default {
  name: 'StoryForm',
  components: { vSelect, quillEditor, ChartBuilder, MapPreview },
  directives: { Ripple },
  data() {
    return {
      editorOption: {
        modules: {
          toolbar: false,
          keyboard: {
            bindings: {
              enter: {
                key: 'Enter',
                handler: () => true,
              },
            },
          },
        },
        theme: 'bubble',
        placeholder: 'Tulis narasi di sini... (select teks untuk format)',
      },
      showDismissibleAlert: false,
      messages: '',
      saving: false,
      autoSaveStatus: 'Draft',
      hoveredBlock: null,
      addMenuOpenAt: null,
      availableYears: {},
      config: { api: '/content', redirect: 'stories' },
      model: {
        title: '',
        lang: 'id',
        status: 'active',
        type: 'story',
        image: null,
        data: { blocks: [] },
      },
      imageFile: null,
      imagePreview: null,
      indicators: [],
    }
  },
  mounted() {
    this.fetchIndicators()
    this.getData()
    this.$nextTick(() => {
      if (this.$refs.titleInput) {
        this.$refs.titleInput.focus()
      }
    })
    // Close add menu on outside click
    document.addEventListener('click', this.closeAddMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAddMenu)
  },
  methods: {
    fetchIndicators() {
      axios.get('/indicator?per_page=500&orderby=name&sort=asc').then(res => {
        this.indicators = res.data.data.rows || res.data.data || []
      })
    },
    getData() {
      if (this.$route.meta.action === 'edit') {
        axios.get(`${this.config.api}/${this.$route.params.id}`).then(res => {
          const d = res.data.data
          if (!d.data) d.data = { blocks: [] }
          if (!d.data.blocks) d.data.blocks = []
          // Ensure each block has _uid
          d.data.blocks.forEach(b => { if (!b._uid) b._uid = ++uid })
          this.model = d
          if (this.model.image_url) this.imagePreview = this.model.image_url
          // Fetch years for existing map blocks
          this.model.data.blocks.forEach(b => {
            if (b.type === 'map') {
              if (b.filters && b.filters.custom_time) b.filters.custom_time = String(b.filters.custom_time)
              if (b.indicator_id) this.fetchAvailableYears(b.indicator_id)
            }
          })
        })
      } else {
        // Start with one empty text block
        this.model.data.blocks = [{ type: 'text', content: '', _uid: ++uid }]
      }
    },

    // Cover Image
    triggerImageUpload() {
      this.$refs.coverInput.click()
    },
    onImageChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.imagePreview = URL.createObjectURL(file)
        this.model.image = file
      }
    },
    removeCover() {
      this.imagePreview = null
      this.imageFile = null
      this.model.image = null
    },

    // Block Image
    triggerBlockImageUpload(index) {
      const ref = this.$refs['imgInput_' + index]
      if (ref && ref[0]) ref[0].click()
      else if (ref) ref.click()
    },
    onBlockImageChange(e, block) {
      const file = e.target.files[0]
      if (file) {
        this.$set(block, 'imageFile', file)
        this.$set(block, 'imagePreview', URL.createObjectURL(file))
      }
    },

    // Add Menu
    toggleAddMenu(index) {
      this.addMenuOpenAt = this.addMenuOpenAt === index ? null : index
    },
    closeAddMenu() {
      this.addMenuOpenAt = null
    },

    // Insert block AFTER the given index (-1 = at end)
    insertBlock(type, afterIndex) {
      const newBlock = this.createBlock(type)
      if (afterIndex === -1 || afterIndex >= this.model.data.blocks.length - 1) {
        this.model.data.blocks.push(newBlock)
      } else {
        this.model.data.blocks.splice(afterIndex + 1, 0, newBlock)
      }
      this.addMenuOpenAt = null
      // If text, focus the new editor
      if (type === 'text') {
        this.$nextTick(() => {
          const refKey = 'editor_' + (afterIndex + 1)
          const editorRef = this.$refs[refKey]
          if (editorRef) {
            const editor = Array.isArray(editorRef) ? editorRef[0] : editorRef
            if (editor && editor.quill) editor.quill.focus()
          }
        })
      }
      // Trigger image upload immediately if image block
      if (type === 'image') {
        this.$nextTick(() => {
          const idx = afterIndex === -1 ? this.model.data.blocks.length - 1 : afterIndex + 1
          this.triggerBlockImageUpload(idx)
        })
      }
    },
    createBlock(type) {
      const base = { _uid: ++uid, type, caption: '', title: '' }
      if (type === 'text') return { ...base, content: '' }
      if (type === 'image') return { ...base, imageFile: null, imagePreview: null, image_url: null }
      if (type === 'infobox') return { ...base, imageFile: null, imagePreview: null, image_url: null, title: '', summary: '', button_text: 'Visit Site', button_url: '' }
      return { ...base, indicator_id: null, filters: { interval: 'year', unit: 'Province', custom_time: '2021' } }
    },
    removeBlockImage(block) {
      this.$set(block, 'imagePreview', null)
      this.$set(block, 'imageFile', null)
      this.$set(block, 'image_url', null)
    },
    removeBlock(index) {
      this.model.data.blocks.splice(index, 1)
    },
    moveBlock(index, direction) {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < this.model.data.blocks.length) {
        const arr = [...this.model.data.blocks]
        const temp = arr[index]
        arr[index] = arr[newIndex]
        arr[newIndex] = temp
        this.model.data.blocks = arr
      }
    },

    getIndicatorInfo(id) {
      return this.indicators.find(i => i.id === id) || null
    },
    async fetchAvailableYears(indicatorId) {
      if (!indicatorId) return;
      if (this.availableYears[indicatorId]) return;
      try {
        const res = await axios.get(`${API_BASE}/v1/indicator_data/chart?indicator_id=${indicatorId}&interval=year`);
        const data = res.data.data;
        const years = new Set();
        if (data && typeof data === 'object') {
          Object.values(data).forEach(region => {
            if (Array.isArray(region)) {
              region.forEach(item => {
                 const d = new Date(item.date);
                 if (!isNaN(d.getFullYear())) years.add(String(d.getFullYear()));
              });
            }
          });
        }
        const sorted = Array.from(years).sort((a,b) => Number(b) - Number(a));
        this.$set(this.availableYears, indicatorId, sorted.length ? sorted : ['2021']);
      } catch (e) {
        this.$set(this.availableYears, indicatorId, ['2021']);
      }
    },

    autoResizeTitle() {
      const el = this.$refs.titleInput
      if (el) {
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
      }
    },
    focusFirstBlock() {
      if (this.model.data.blocks.length > 0) {
        const ref = this.$refs['editor_0']
        const editor = Array.isArray(ref) ? ref[0] : ref
        if (editor && editor.quill) editor.quill.focus()
      }
    },
    focusLastTextBlock() {
      // Don't steal focus from clicking inside blocks
    },

    // Save
    save() {
      if (!this.model.title.trim()) {
        this.showDismissibleAlert = true
        this.messages = 'Judul story tidak boleh kosong.'
        return
      }
      this.saving = true
      this.autoSaveStatus = 'Menyimpan...'

      const formData = new FormData()
      formData.append('title', this.model.title)
      formData.append('lang', this.model.lang)
      formData.append('status', this.model.status)
      formData.append('type', 'story')

      // Clean blocks before saving (remove runtime-only fields)
      const blocksToSave = this.model.data.blocks.map(b => {
        const clean = { ...b }
        delete clean._uid
        delete clean.imagePreview
        // handle image files for blocks
        return clean
      })

      // Append block image files separately
      this.model.data.blocks.forEach((b, i) => {
        if ((b.type === 'image' || b.type === 'infobox') && b.imageFile instanceof File) {
          formData.append(`block_image_${i}`, b.imageFile)
          blocksToSave[i].blockImageKey = `block_image_${i}`
        }
      })

      formData.append('data', JSON.stringify({ blocks: blocksToSave }))

      if (this.model.image instanceof File) {
        formData.append('image', this.model.image)
      }

      const method = this.$route.meta.action === 'edit' ? 'put' : 'post'
      const url = this.$route.meta.action === 'edit'
        ? `${this.config.api}/${this.$route.params.id}`
        : this.config.api

      axios({ method, url, data: formData })
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Story berhasil disimpan.', {
            title: 'Berhasil',
            variant: 'success',
            solid: true,
          })
          if (this.$route.meta.action !== 'edit') {
            this.$router.push({ name: this.config.redirect })
          }
        })
        .catch(e => {
          this.showDismissibleAlert = true
          this.messages = e.response?.data?.message || 'Terjadi kesalahan. Coba lagi.'
          this.autoSaveStatus = 'Gagal disimpan'
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
